import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Loader from '../../../component/common/Loader';
import { CompanyApi } from '../../../api/companyApi';
import moment from 'moment';
import Table from '../../../component/common/Table';
import Pagination from '../../../component/common/table2/Pagination';
import { TableCard } from '../../../component/common/designSystem';

const Summary = () => {
  const location = useLocation();
  const state = location.state;
  const [loader, setLoader] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (state?._id) {
      fetchChallengeSummary(state._id);
    }
  }, [state?._id]);

  useEffect(() => {
    if (state?._id) {
      const timer = setTimeout(() => {
        fetchChallengeParticipants(state._id, currentPage, recordsPerPage);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [state?._id, currentPage, recordsPerPage, searchTerm]);

  const fetchChallengeSummary = async (id) => {
    setLoader(true);
    try {
      const response = await CompanyApi.getChallengeSummary(id);
      if (response?.data.data) {
        setSummaryData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching challenge summary:', error);
    } finally {
      setLoader(false);
    }
  };

  const fetchChallengeParticipants = async (id, page, limit) => {
    setLoader(true);
    try {
      const response = await CompanyApi.getChallengeParticipants(id, page, limit, searchTerm);
      if (response?.data?.data) {
        setParticipants(response.data.data.users || []);
        setTotalCount(response.data.data.pagination.totalUsers || 0);
      }
    } catch (error) {
      console.error('Error fetching challenge participants:', error);
    } finally {
      setLoader(false);
    }
  };

  const handlePagination = (page) => {
    setCurrentPage(page);
  };


  const getRankBadge = (rank) => {
    const badges = {
      1: { icon: '🥇', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
      2: { icon: '🥈', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
      3: { icon: '🥉', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' }
    };
    return badges[rank] || { icon: '', color: '' };
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      sortable: false,
      align: 'left',
      type: 'text'
    },
    {
      title: 'Email',
      key: 'email',
      sortable: false,
      align: 'left',
      type: 'text'
    },
    {
      title: 'Rank',
      key: 'rank',
      sortable: false,
      align: 'center',
      type: 'text'
    },
    {
      title: 'Total Steps',
      key: 'totalSteps',
      sortable: false,
      align: 'center',
      type: 'text'
    },
    {
      title: 'Days Walked',
      key: 'daysWalked',
      sortable: false,
      align: 'center',
      type: 'text'
    },
    {
      title: 'Joined At',
      key: 'joinedAt',
      sortable: false,
      align: 'center',
      type: 'text'
    }
  ];

  const rowsData = participants.map((participant) => ({
    _id: participant.userId,
    name: <div className="text-sm">{participant.name || 'N/A'}</div>,
    email: <div className="text-sm">{participant.email || 'N/A'}</div>,
    rank: (
      <div className="text-sm">
        {participant.rank || 'N/A'}
      </div>
    ),
    totalSteps: (
      <div className="text-sm">
        {participant.totalStepsWalked?.toLocaleString() || 0}
      </div>
    ),
    daysWalked: (
      <div className="text-sm">
        {participant.totalDaysWalked || 0}
      </div>
    ),
    joinedAt: (
      <div className="text-sm">
        {participant.challengeJoinedAt ? moment(participant.challengeJoinedAt).format('MMM DD, YYYY') : 'N/A'}
      </div>
    )
  }));

  if (!summaryData) {
    return (
      <div className="flex justify-center items-center py-12">
        {loader && <Loader />}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1640px]">
      {loader && <Loader />}
      
      {/* Challenge Overview Section */}
     

      {/* Challenge Statistics Section */}
      <div className="flex flex-col w-full mt-5 bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder border-[1.5px] border-shoorah-borderGray lg:rounded-2xl rounded-xl my-5 lg:p-6 p-4 pb-8">
        <div className="flex justify-between items-center mt-6 mb-6">
          <h3 className="font-medium text-xl">Challenge Statistics</h3>
        </div>

        <div className="grid lg:gap-5 gap-4 md:grid-cols-3 grid-cols-1">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-5 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600 dark:text-blue-400 mb-1">Total Participants</p>
                <p className="text-3xl font-bold text-blue-900 dark:text-blue-100">
                  {summaryData?.totalParticipants || 0}
                </p>
              </div>
              <div className="text-4xl">👥</div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl p-5 border border-green-200 dark:border-green-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600 dark:text-green-400 mb-1">Total Steps</p>
                <p className="text-3xl font-bold text-green-900 dark:text-green-100">
                  {summaryData?.totalStepsInChallenge?.toLocaleString() || 0}
                </p>
              </div>
              <div className="text-4xl">👣</div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-xl p-5 border border-purple-200 dark:border-purple-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600 dark:text-purple-400 mb-1">Rank Distribution</p>
                <div className="flex gap-2 mt-2">
                  <span className="text-sm font-semibold text-purple-900 dark:text-purple-100">
                    🥇 {summaryData?.rankCounts?.rank1 || 0}
                  </span>
                  <span className="text-sm font-semibold text-purple-900 dark:text-purple-100">
                    🥈 {summaryData?.rankCounts?.rank2 || 0}
                  </span>
                  <span className="text-sm font-semibold text-purple-900 dark:text-purple-100">
                    🥉 {summaryData?.rankCounts?.rank3 || 0}
                  </span>
                </div>
              </div>
              <div className="text-4xl">🏆</div>
            </div>
          </div>
        </div>
      </div>

      {/* Top 3 Ranked Users Section */}
      {summaryData?.top3RankedUsers && summaryData.top3RankedUsers.length > 0 && (
        <div className="flex flex-col w-full mt-5 bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder border-[1.5px] border-shoorah-borderGray lg:rounded-2xl rounded-xl my-5 lg:p-6 p-4 pb-8">
          <div className="flex justify-between items-center mt-6 mb-6">
            <h3 className="font-medium text-xl">Top 3 Ranked Users</h3>
          </div>

          <div className="grid lg:gap-5 gap-4 md:grid-cols-3 grid-cols-1">
            {summaryData.top3RankedUsers.map((user, index) => {
              const rankBadge = getRankBadge(user.rank);
              return (
                <div
                  key={user.userId}
                  className="bg-white dark:bg-shoorah-darkBgColor rounded-xl p-5 border-2 border-gray-200 dark:border-shoorah-darkBorder hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-center mb-4">
                    <div className="relative">
                      {user.profilePicture ? (
                        <img
                          src={user.profilePicture}
                          alt={user.name}
                          className="w-16 h-16 rounded-full object-cover border-2 border-gray-300 dark:border-gray-600"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xl font-bold">
                          {user.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      )}
                      <div className={`absolute -top-2 -right-2 w-8 h-8 rounded-full ${rankBadge.color} flex items-center justify-center text-lg font-bold border-2 border-white dark:border-gray-800`}>
                        {rankBadge.icon}
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <h4 className="font-semibold text-lg text-gray-900 dark:text-white truncate">
                        {user.name}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                        {user.email}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Steps</span>
                      <span className="text-sm font-bold text-gray-900 dark:text-white">
                        {user.totalSteps?.toLocaleString() || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Days Walked</span>
                      <span className="text-sm font-bold text-gray-900 dark:text-white">
                        {user.totalDaysWalked || 0} days
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Rank</span>
                      <span className={`text-sm font-bold px-3 py-1 rounded-full ${rankBadge.color}`}>
                        #{user.rank}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Challenge Participants Section */}
      <div className="flex flex-col w-full mt-5 bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder border-[1.5px] border-shoorah-borderGray lg:rounded-2xl rounded-xl my-5 lg:p-6 p-4 pb-8">
        <div className="flex justify-between items-center mt-6 mb-6">
          <h3 className="font-medium text-xl">Challenge Participants</h3>
          <div className="relative">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-64 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-shoorah-primary dark:bg-shoorah-darkBgColor dark:text-white"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: recordsPerPage,
            onPageChange: handlePagination
          }}
        >
          <Table
            columns={columns}
            data={rowsData}
            name={'challenge_participants'}
            bottomBorder={totalCount > recordsPerPage}
            loader={loader}
          />
        </TableCard>
        </div>

        {totalCount > recordsPerPage && (
          <div className="mt-5">
            
          </div>
        )}
      </div>
    </div>
  );
};

export default Summary;
